import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { attemptsApi } from '@/shared/api/endpoints';

/**
 * How often the learner has played each limited fragment of an open attempt.
 *
 * The count belongs to the attempt, not to the widget that plays the sound: the
 * widget is created anew whenever the learner switches between exercises, and a
 * limit that restarted with it would be no limit. The counts are persisted, so
 * reloading the page does not refill them either.
 */
export interface PlayCountsState {
  /** By `playKey`. Insertion order is age, which is what the cap trims by. */
  counts: Record<string, number>;
}

/** Enough for every open attempt of a device; older entries belong to attempts long gone. */
const MAX_ENTRIES = 400;

const initialState: PlayCountsState = { counts: {} };

export const playKey = (attemptId: string, itemIndex: number, stimulus: number) =>
  `${attemptId}:${itemIndex}:${stimulus}`;

const playCountsSlice = createSlice({
  name: 'playCounts',
  initialState,
  reducers: {
    played(state, action: PayloadAction<string>) {
      state.counts[action.payload] = (state.counts[action.payload] ?? 0) + 1;
      const keys = Object.keys(state.counts);
      for (const key of keys.slice(0, Math.max(keys.length - MAX_ENTRIES, 0))) {
        delete state.counts[key];
      }
    },
  },
  extraReducers: (builder) => {
    // A closed attempt can be replayed freely, so its counts are of no further use.
    builder.addMatcher(
      isAnyOf(
        attemptsApi.endpoints.startAttempt.matchFulfilled,
        attemptsApi.endpoints.getAttempt.matchFulfilled,
        attemptsApi.endpoints.finishAttempt.matchFulfilled,
      ),
      (state, action) => {
        const attempt = action.payload;
        if (attempt.status === 'in_progress') return;
        for (const key of Object.keys(state.counts)) {
          if (key.startsWith(`${attempt.id}:`)) delete state.counts[key];
        }
      },
    );
  },
});

export const { played } = playCountsSlice.actions;
export const playCountsReducer = playCountsSlice.reducer;

export interface WithPlayCounts {
  playCounts: PlayCountsState;
}

export const selectPlayCounts = (state: WithPlayCounts) => state.playCounts.counts;

export function isPlayCountsState(value: unknown): value is PlayCountsState {
  if (typeof value !== 'object' || value === null) return false;
  const counts = (value as Partial<PlayCountsState>).counts;
  return (
    typeof counts === 'object' &&
    counts !== null &&
    Object.values(counts).every((count) => typeof count === 'number')
  );
}
